import React, { useState, useEffect } from "react";
import {
  UserGroupIcon,
  UserIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { adminApi } from "../../../repositories/admin";
import { Skeleton } from "../../../components/ui/skeleton";

interface AccountDetails {
  totalUsers: number;
  loggedInUsers: number;
  recentAccountsLogged: number;
}

interface DspSetUpAccounts {
  dsp_credentials_set_up_accounts: number;
  one_pitch_submitted_accounts: number;
  two_pitch_submitted_accounts: number;
}

interface TimingMetrics {
  average_account_to_first_pitch: string;
  average_first_to_second_pitch: string;
  users_with_first_pitch: number;
  users_with_second_pitch: number;
}

const DashboardMetrics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [dspAccounts, setDspAccounts] = useState<DspSetUpAccounts | null>(null);
  const [timingMetrics, setTimingMetrics] = useState<TimingMetrics | null>(null);

  useEffect(() => {
    fetchAllMetrics();
  }, []);

  const fetchAllMetrics = async () => {
    try {
      setLoading(true);
      const [accountResponse, dspResponse, timingResponse] = await Promise.all([
        adminApi.getAllAccountDetails(),
        adminApi.getAllDspSetUpAccounts(),
        adminApi.getTimingMetrics(),
      ]);

      if (accountResponse?.data?.success) {
        setAccountDetails(accountResponse.data.data);
      }
      if (dspResponse?.data?.success) {
        setDspAccounts(dspResponse.data.data);
      }
      if (timingResponse?.data?.success) {
        setTimingMetrics(timingResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (value: number, total: number): string => {
    if (!total) return "0";
    return ((value / total) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[3, 3, 2].map((count, section) => (
          <div key={section}>
            <Skeleton className="w-48 h-5 mb-4" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3" aria-busy="true">
              {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="p-4 bg-white border rounded-lg border-neutral-200">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Skeleton className="w-28 h-3" />
                      <Skeleton className="w-16 h-6" />
                    </div>
                    <Skeleton className="w-10 h-10 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const totalUsers = accountDetails?.totalUsers || 0;
  const loggedInUsers = accountDetails?.loggedInUsers || 0;
  const recentLogins = accountDetails?.recentAccountsLogged || 0;

  const dspSetup = dspAccounts?.dsp_credentials_set_up_accounts || 0;
  const onePitch = dspAccounts?.one_pitch_submitted_accounts || 0;
  const twoPitches = dspAccounts?.two_pitch_submitted_accounts || 0;

  const avgAccountToFirst = timingMetrics?.average_account_to_first_pitch || "N/A";
  const avgFirstToSecond = timingMetrics?.average_first_to_second_pitch || "N/A";

  return (
    <div>
      {/* Account Creation & Login Section */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-black">Account Creation & Login</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">Total Accounts Created</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {totalUsers}
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <UserGroupIcon className="w-5 h-5" />
              </span>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">Accounts Logged In (Ever)</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {loggedInUsers}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {calculatePercentage(loggedInUsers, totalUsers)}% of total
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <UserIcon className="w-5 h-5" />
              </span>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">Logged In (Last 7 Days)</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {recentLogins}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {calculatePercentage(recentLogins, totalUsers)}% of total
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <ClockIcon className="w-5 h-5" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Activation Metrics Section */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-black">Activation Metrics</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">DSP Credentials Set Up</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {dspSetup}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {dspSetup}/{totalUsers} accounts ({calculatePercentage(dspSetup, totalUsers)}%)
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <CheckCircleIcon className="w-5 h-5" />
              </span>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">Submitted 1+ Pitches</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {onePitch}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {onePitch}/{totalUsers} accounts ({calculatePercentage(onePitch, totalUsers)}%)
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <ChartBarIcon className="w-5 h-5" />
              </span>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">Submitted 2+ Pitches</p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {twoPitches}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {twoPitches}/{totalUsers} accounts ({calculatePercentage(twoPitches, totalUsers)}%)
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <ChartBarIcon className="w-5 h-5" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Timing Metrics Section */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold text-black">Timing Metrics</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">
                  Avg. Time: Account Creation → First Pitch
                </p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {avgAccountToFirst}
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <ClockIcon className="w-5 h-5" />
              </span>
            </div>
          </div>

          <div className="p-4 bg-white border rounded-lg border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-neutral-500 sm:text-sm">
                  Avg. Time: First Pitch → Second Pitch
                </p>
                <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                  {avgFirstToSecond}
                </p>
              </div>
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                <ClockIcon className="w-5 h-5" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMetrics;
